import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { Tabs, Tab, Box, Button, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Col, Row } from 'antd';
import { styles } from './data-model-style';
import AttributesTab from './tabs/AttributesTab';
import CleanseTab from './tabs/CleanseTab';
import MatchTab from './tabs/MatchTab';
import SurvivorshipTab from './tabs/SurvivorshipTab';
import RelationshipTypesTab from './tabs/RelationshipTypesTab';
import LoadDataModal from './load-data/LoadDataModal';
import AddEntity from './entity/AddEntity';
import DataModelExecutionTab from './tabs/DataModelExecutionTab';
import LoadingIcon from '../../reusable-component/LoadingIcon';
import {
  getAllEntitiesByEngagementId,
  getEntityDetailsByEntityId,
  dataModelExecutionList,
} from "../../api's/DataModelApi";
import GoldenRecordsTab from './tabs/GoldenRecordsTab';
import NeoGraph from './graph-component/NeoGraph';

export default function DataModelDesigner({ title, engagementId }) {
  const { enqueueSnackbar } = useSnackbar();

  const [entities, setEntities] = useState([]);
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [selectedEntityName, setSelectedEntityName] = useState('');
  const [description, setDescription] = useState('');
  const [attributes, setAttributes] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [cleanse, setCleanse] = useState([]);
  const [survivorship, setSurvivorship] = useState([]);
  const [matchRulesData, setMatchRulesData] = useState([]);
  const [tabValue, setTabValue] = useState('Attributes');
  const [openLoadDataModal, setOpenLoadDataModal] = useState(false);
  const [openAddEntity, setOpenAddEntity] = useState(false);
  const [nodeId, setNodeId] = useState('');
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Split loading into two parts
  const [entityListLoading, setEntityListLoading] = useState(false);
  const [entityDetailsLoading, setEntityDetailsLoading] = useState(false);

  // Fetch entities initially
  const fetchEntities = async () => {
    try {
      setEntityListLoading(true);
      const res = await getAllEntitiesByEngagementId(engagementId);
      const data = res?.data || [];
      if (data.length > 0) {
        setEntities(data);
        setSelectedEntityId(data[0].id);
        setSelectedEntityName(data[0].name);
      } else {
        enqueueSnackbar('No entities found for this engagement', {
          variant: 'info',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      }
    } catch (err) {
      console.error('Error fetching entities:', err);
    } finally {
      setEntityListLoading(false);
    }
  };
  useEffect(() => {
    fetchEntities();
  }, [engagementId]);

  // Fetch entity details (reusable for initial load and after save)
  const fetchEntityDetails = useCallback(async () => {
    if (!selectedEntityId) return;
    try {
      setEntityDetailsLoading(true);
      const res = await getEntityDetailsByEntityId(engagementId, selectedEntityId);
      const entity = res?.data;
      if (entity) {
        setSelectedEntityName(entity.name || '');
        setDescription(entity.description || '');
        setAttributes(entity.attributes || []);
        setCleanse(entity.cleanseRules || []);
        setMatchRulesData(entity.matchRules || []);
        setNodeId(entity.nodeId || '');
        setSurvivorship([]);
        setRelationships([]);
      }
    } catch (err) {
      console.error('Error fetching entity details:', err);
      enqueueSnackbar('Failed to load entity details', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    } finally {
      setEntityDetailsLoading(false);
    }
  }, [selectedEntityId, engagementId, enqueueSnackbar]);

  // Fetch details when entity changes
  useEffect(() => {
    fetchEntityDetails();
  }, [fetchEntityDetails]);

  const fetchExecutions = async () => {
    setLoading(true);
    try {
      const entityId = selectedEntityId; // use the selected entity ID
      const data = await dataModelExecutionList(entityId);
      console.log('Fetched Executions:', data);
      setExecutions(data); // ✅ assuming backend returns a list
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshRow = (executionId, updatedExecution) => {
    setExecutions((prev) => prev.map((e) => (e.id === executionId ? updatedExecution : e)));
  };

  useEffect(() => {
    if (tabValue === 'RunHistory') {
      fetchExecutions();
    }
  }, [tabValue, selectedEntityId]);

  const handleLoadDataModal = () => setOpenLoadDataModal(!openLoadDataModal);

  if (entityListLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10%' }}>
        <LoadingIcon />
      </div>
    );
  }

  if (entities.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', mt: 10 }}>
        <Typography variant="h6">No entities found.</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => setOpenAddEntity(true)}>
          + Add Entity
        </Button>
        <AddEntity
          open={openAddEntity}
          onSuccess={fetchEntities}
          onClose={() => setOpenAddEntity(false)}
          engagementId={engagementId}
        />
      </Box>
    );
  }

  return (
    <div style={styles.container}>
      <AddEntity
        open={openAddEntity}
        onSuccess={fetchEntities}
        onClose={() => setOpenAddEntity(false)}
        engagementId={engagementId}
      />

      <Row
        gutter={[8, 8]}
        style={{
          padding: '8px 0',
          margin: '0 auto',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        {/* SIDEBAR */}
        <Col xs={24} sm={7} md={6} lg={5} xl={5}>
          <div style={styles.sidebar}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={styles.sidebarTitle}>Entities</div>
              <div>
                <button style={styles.button} onClick={() => setOpenAddEntity(true)}>
                  + Add Entity
                </button>
              </div>
            </div>

            {entities.map((entity) => (
              <div
                key={entity.id}
                role="button"
                tabIndex={0}
                style={{
                  ...styles.entityItem,
                  ...(selectedEntityId === entity.id ? styles.entityItemActive : {}),
                }}
                onClick={() => {
                  if (selectedEntityId !== entity.id) {
                    setSelectedEntityId(entity.id);
                    setSelectedEntityName(entity.name);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setSelectedEntityId(entity.id);
                    setSelectedEntityName(entity.name);
                  }
                }}
              >
                {entity.name}
              </div>
            ))}
          </div>
        </Col>

        {/* MAIN PANEL */}
        <Col xs={24} sm={17} md={18} lg={19} xl={19}>
          <div
            style={{
              ...styles.entityPanel,
              minWidth: 0, // CRITICAL: allows flex item to shrink below content size
            }}
          >
            <div style={styles.entityHeader}>
              <div>
                <h2 style={styles.entityTitle}>Entity: {selectedEntityName}</h2>
                <p style={styles.entitySubtitle}>{description}</p>
              </div>
              <div>
                <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={handleLoadDataModal}>
                  Load Data
                </Button>
              </div>
            </div>

            {/* Show loader only inside details area */}
            {entityDetailsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15%' }}>
                <LoadingIcon />
              </div>
            ) : (
              <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
                  <Tabs
                    value={tabValue}
                    onChange={(e, newValue) => setTabValue(newValue)}
                    textColor="primary"
                    indicatorColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ display: 'flex' }}
                  >
                    {/* Left-aligned tabs */}
                    <Tab label="Attributes" value="Attributes" />
                    <Tab label="Cleanse" value="Cleanse" />
                    <Tab label="Match" value="Match" />
                    <Tab label="Survivorship" value="Survivorship" />
                    <Tab label="Relationship Types" value="RelationshipTypes" />
                    <Tab label="Golden Records" value="GoldenRecords" />
                    {/* <Tab label="Graph" value="Graph" /> */}

                    {/* Spacer pushes the next tab to the right */}
                    <Box sx={{ flexGrow: 1 }} />

                    {/* Right-aligned tab */}
                    <Tab label="Run History" value="RunHistory" />
                  </Tabs>
                </Box>

                <div
                  style={{
                    // width: '100%',
                    // maxWidth: '100%',
                    // overflow: 'hidden',
                    // boxSizing: 'border-box',
                  }}
                >
                  {tabValue === 'Attributes' && (
                    <AttributesTab attributes={attributes} setAttributes={setAttributes} entityId={selectedEntityId} onEntityRefresh={fetchEntityDetails} />
                  )}
                  {tabValue === 'Cleanse' && (
                    <CleanseTab
                      attributes={attributes}
                      entities={entities}
                      cleanse={cleanse}
                      entityName={selectedEntityName}
                      entityId={selectedEntityId}
                    />
                  )}
                  {tabValue === 'Match' && (
                    <MatchTab
                      nodeId={nodeId}
                      entityId={selectedEntityId}
                      attributes={attributes}
                      matchRulesData={matchRulesData}
                    />
                  )}
                  {tabValue === 'Survivorship' && (
                    <SurvivorshipTab entityId={selectedEntityId} attributes={attributes} survivorship={survivorship} />
                  )}
                  {tabValue === 'RelationshipTypes' && <RelationshipTypesTab relationships={relationships} />}
                  {tabValue === 'GoldenRecords' && <GoldenRecordsTab nodeId={nodeId} />}
                  {tabValue === 'RunHistory' && (
                    <DataModelExecutionTab
                      dataModelExecution={executions}
                      loading={loading}
                      error={error}
                      onRefreshRow={handleRefreshRow}
                    />
                  )}
                  {tabValue === 'Graph' && <NeoGraph />}
                </div>
              </>
            )}
          </div>
        </Col>
      </Row>
      <LoadDataModal
        show={openLoadDataModal}
        onClose={handleLoadDataModal}
        onSave={() => { }}
        attributes={attributes}
        selectedEntityId={selectedEntityId}
      />
    </div>
  );
}

DataModelDesigner.propTypes = {
  title: PropTypes.string.isRequired,
  engagementId: PropTypes.string.isRequired,
};
